// Third Party
import React from 'react';
import classNames from 'classnames';

// Utils
import { formatMessage } from '@/utils/i18n';
import { statusesConfig, ShelfStatus as ShelfStatusType } from '@/utils/shelf-management';

// Components
import Tooltip from '@/views/components/tooltips/Tooltip';
import ConnectedPrivateComponent from '@/views/components/PrivateComponent';
import ShelfStatusIcon from './ShelfStatusIcon';

type Props = {
  isV2?: boolean;
  statuses: string[];
  title?: string;
  tooltipClassName?: string;
};

// List of statuses (as strings) supported by the component
const supportedStatuses = [statusesConfig.shelf_onShelf.key, statusesConfig.shelf_onShelfKYPComplete.key];

const ShelfStatus = ({ isV2, statuses, title, tooltipClassName }: Props) => {
  const filteredStatuses = supportedStatuses.filter(status => statuses.includes(status));
  const displayTitle = title || formatMessage('ShelfStatusIndicator.defaultTitle');

  const renderIcon = (status: ShelfStatusType): JSX.Element => {
    if (isV2) {
      const statusClassName = classNames({
        'icon-ds-check-circle-outline on-shelf': status === statusesConfig.shelf_onShelf.key,
        'icon-ds-check-circle on-shelf-kyp': status === statusesConfig.shelf_onShelfKYPComplete.key,
      });

      return <i data-testid="shelf-status-icon" className={classNames('shelf-status-icon', statusClassName)} />;
    }
    const statusClassName = classNames({
      'on-shelf': status === statusesConfig.shelf_onShelf.key,
      'on-shelf-kyp': status === statusesConfig.shelf_onShelfKYPComplete.key,
    });

    return (
      <div data-testid="shelf-status-icon">
        <ShelfStatusIcon className={statusClassName} />
      </div>
    );
  };

  const renderTooltipMessage = (status: ShelfStatusType, name: string): string => {
    if (isV2) {
      return formatMessage(`ShelfStatusIndicator.tooltip_${statusesConfig[status].key}`, { name });
    }
    return formatMessage(`ShelfStatusIndicator.${statusesConfig[status].key}`);
  };

  const renderTitle = (): JSX.Element | null => {
    if (isV2) {
      return <span className="shelf-status-title">{displayTitle}</span>;
    }
    return null;
  };

  if (filteredStatuses.length > 0) {
    // This component should only render one status checkmark, hence why only the last status
    // of the list is rendered. The status order in supportedStatuses is what defines which
    // status has precedence.
    const status = filteredStatuses.pop();
    const shelfStatusClassName = classNames({
      'shelf-status-v2': isV2,
      'shelf-status': !isV2,
      'on-shelf': status === statusesConfig.shelf_onShelf.key,
      'on-shelf-kyp': status === statusesConfig.shelf_onShelfKYPComplete.key,
    });

    if (status) {
      return (
        <ConnectedPrivateComponent permissions={statusesConfig[status].permissions}>
          {() => (
            <span className={shelfStatusClassName}>
              <Tooltip className={tooltipClassName} key={status} message={renderTooltipMessage(status, displayTitle)}>
                <span className="shelf-status-content">
                  {renderIcon(status)}
                  {renderTitle()}
                </span>
              </Tooltip>
            </span>
          )}
        </ConnectedPrivateComponent>
      );
    }
  }

  return null;
};

export default ShelfStatus;
